import { useState, useEffect, useRef } from 'react';
import Head from 'next/head';

export default function Home() {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [copied, setCopied] = useState({
    fullWidget: false,
    calendarWidget: false,
    apiExample: false,
    wordpressShortcode: false
  });

  
  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied({...copied, [type]: true});
      setTimeout(() => {
        setCopied({...copied, [type]: false});
      }, 2000);
    });
  };
  
  useEffect(() => {
    // Load widget script
    const script = document.createElement('script');
    script.src = '/widget/kalenderwidget.js?v=' + new Date().getTime(); // Add timestamp to force reload
    script.async = true;
    script.onload = () => {
      setScriptLoaded(true);
      
      // Initialize widgets after script is loaded
      if (window.EmeraldBookingWidget) {
        // Clear any existing widgets
        const fullContainer = document.getElementById('booking-widget-container');
        
        // Make sure containers are empty
        if (fullContainer) fullContainer.innerHTML = '';
        
        // Full widget (default)
        window.EmeraldBookingWidget.init({
          container: fullContainer,
          widgetBaseUrl: 'https://booking.emerald-system.id',
          primaryColor: '#1a1a1a', // Black
          mode: 'calendar',
          propertyCode: ['DEMO'],
          database: ['DEMO'],
          hotelname: ['Demo Hotel'],
          debug: true,
          language: 'id'
        });
      }
    };
    document.body.appendChild(script);
    
    return () => {
      // Clean up script on unmount
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const fullWidgetCode = `<!-- Gaya CSS untuk Container (Glassmorphism Effect) -->
<style>
  .booking-wrapper {
    max-width: 900px;
    margin: 40px auto;
    background-image: url('https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80');
    background-size: cover;
    background-position: center;
    border-radius: 24px;
    padding: 32px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    min-height: 500px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
  }
  
  /* Overlay Gelap + Blur */
  .booking-wrapper::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.2);
    backdrop-filter: blur(2px);
    border-radius: 24px;
    z-index: 1;
  }
  
  /* Container Widget */
  #booking-widget-container {
    width: 100%;
    position: relative;
    z-index: 10;
    padding: 0 8px;
  }
</style>

<!-- Container Widget di Halaman -->
<div class="booking-wrapper">
  <div id="booking-widget-container"></div>
</div>

<!-- Script Widget -->
<script src="https://widget2.emerald-system.id/widget/kalenderwidget.js"></script>

<script>
  // Inisialisasi Widget
  document.addEventListener('DOMContentLoaded', function() {
    EmeraldBookingWidget.init({
      container: document.getElementById('booking-widget-container'),
      widgetBaseUrl: 'https://booking.emerald-system.id',
      primaryColor: '#1a1a1a', // Sesuaikan warna tombol
      accentColor: '#facc15', // Sesuaikan warna highlight (Gold)
      propertyCode: ['DEMO', 'PMH'], // Ganti dengan kode properti Anda
      database: ['DEMO', 'PMH'], // Ganti dengan database Anda
      hotelname: ['Demo Hotel', 'Hotel Pakumas'], // Nama hotel
      language: 'id', // 'id' atau 'en'
      showPropertySelect: true
    });
  });
</script>`;

  return (
    <>
      <Head>
        <title>Emerald Booking Widget - Elegant Edition</title>
        <meta name="description" content="Demo penggunaan Emerald Booking Widget Elegant" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Playfair+Display:wght@600;700&display=swap" rel="stylesheet" />
      </Head>
      
      <style jsx global>{`
        :root {
          --primary-color: #be123c;
          --em-font: 'Inter', sans-serif;
        }
        body {
            background: #f8fafc;
            font-family: var(--em-font);
        }
      `}</style>
      
      <div className="min-h-screen flex flex-col">
        <header className="bg-gradient-to-r from-rose-800 to-rose-900 text-white py-12 shadow-xl">
          <div className="container mx-auto px-6">
            <h1 className="text-4xl font-bold mb-4 font-serif">Emerald Booking Widget</h1>
            <p className="text-xl opacity-90">Simple Elegant Edition v4.0.1</p>
          </div>
        </header>
      
        <main className="container mx-auto p-6 flex-grow">
          <section id="demo-section" className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Live Demo</h2>
              <p className="text-gray-600">Widget tampilan baru dengan Glassmorphism dan Navigasi Kalender</p>
            </div>
            
            <div className="max-w-4xl mx-auto bg-[url('https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')] bg-cover bg-center rounded-3xl p-8 shadow-2xl min-h-[500px] flex items-center justify-center relative">
               <div className="absolute inset-0 bg-black/20 rounded-3xl backdrop-blur-[2px]"></div>
               <div id="booking-widget-container" className="w-full relative z-10 px-2 md:px-0"></div>
            </div>
          </section>
          
          <section id="integration-section" className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">Cara Integrasi</h2>
            <div className="bg-gray-900 text-gray-100 rounded-xl p-6 shadow-lg overflow-hidden">
                <div className="flex justify-between items-center mb-4">
                    <span className="text-sm text-gray-400">HTML / JavaScript</span>
                    <button onClick={() => copyToClipboard(fullWidgetCode, 'fullWidget')} className="text-xs bg-white/10 hover:bg-white/20 px-3 py-1 rounded transition">
                        {copied.fullWidget ? 'Disalin!' : 'Salin Kode'}
                    </button>
                </div>
                <pre className="font-mono text-sm overflow-x-auto p-4 bg-black/30 rounded-lg">
                    {fullWidgetCode}
                </pre>
            </div>
          </section>
        </main>
      </div>
    </>
  );
}